import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getBrandConfig } from "@/config/brand";

export default function HeroSection() {
  const [searchValue, setSearchValue] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const config = getBrandConfig().hero;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSearch = () => {
    const trimmed = searchValue.trim();
    if (trimmed) {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handlePillClick = (href: string) => {
    navigate(href);
  };

  return (
    <section className="relative min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          fetchpriority="high"
          loading="eager"
          src="https://public.readdy.ai/ai/img_res/edited_3fa36b396cf2122f5137e482a99196a3_c948da92.jpg"
          alt="美業探店背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
      </div>

      {/* Content */}
      <div
        className={`relative z-10 w-full px-4 md:px-8 text-center transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="max-w-3xl mx-auto">
          {/* Main Title */}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-wide">
            {config.title}
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8">
            {config.subtitle}
          </p>

          {/* Search Box */}
          <div className="relative max-w-xl mx-auto">
            <div className="flex items-center bg-white rounded-full overflow-hidden shadow-lg">
              <div className="pl-4 pr-2 text-slate-400">
                <i className="ri-search-line text-lg" />
              </div>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={config.searchPlaceholder}
                className="flex-1 py-3.5 px-2 text-sm text-slate-700 placeholder:text-slate-400 bg-transparent outline-none"
              />
              <button
                onClick={handleSearch}
                className="px-5 py-3.5 bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                搜尋
              </button>
            </div>

            {/* Hot Search Pills */}
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <span className="text-xs text-white/60 flex items-center mr-1">
                <i className="ri-fire-line mr-1" />
                熱門搜尋：
              </span>
              {config.hotSearches.map((pill) => (
                <button
                  key={pill.label}
                  onClick={() => handlePillClick(pill.href)}
                  className="px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium hover:bg-white/25 transition-all border border-white/20 whitespace-nowrap cursor-pointer"
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category Tags */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-6">
            {config.categories.map((cat) => (
              <a
                key={cat.label}
                href={cat.href}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs md:text-sm font-medium hover:bg-white/25 transition-all border border-white/20 whitespace-nowrap"
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}